import React, { useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { useLocation, Outlet, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { useGetMyAccount } from '@queries/Account'

import ErrorFallback from '@components/ErrorBoundary/ErrorBoundaryFallback'
import Loading from '@components/Loading'
import Navigation from '@components/Navigation'
import SlackConnectionPopup from '@components/SlackConnectionPopup'
import LeftNav from '../Navigation/LeftNav'

import { useGetTeamSubscription } from '@queries/Billing'
import useStore from '@src/store'
import useLocalStorage from '@helpers/hooks/useLocalStorage'
import { useGetMaintenanceBanner } from '@helpers/hooks/maintenace/useGetMaintenanceBanner'
import routes, { isFullBleedRoute } from '@src/Routes/routes'

import styles from './index.module.css'
import { SHOW_MAINTENANCE_BANNER } from '@src/constants'
import { showsUpgradeBanner } from '@helpers/subscription'
import { needsOnboarding } from '@helpers/hooks/useNeedsOnboarding'

// Generous enough that a real tour step never trips it, short enough that an
// abandoned tour does not leave the app covered.
const ONBOARDING_LOCK_TIMEOUT_MS = 10_000

export interface LayoutProps {}

export const Layout: React.FC<LayoutProps> = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { data: user } = useGetMyAccount()

  const { data: subscription } = useGetTeamSubscription({ enabled: !!user?.team?.subscription })

  const [maintenace] = useGetMaintenanceBanner()

  const isOnboarding = useStore.use.isOnboarding()
  const startPath = useStore.use.startPath()
  const onboardStep = useStore.use.onboardingStep()
  const setOnboardingStep = useStore.use.setOnboardingStep()

  useEffect(() => {
    if (startPath && location.pathname !== startPath) {
      navigate(startPath)
    }
  }, [])

  const [isCollapsed, setIsCollapsed] = useLocalStorage('isCollapsed', false)

  const isLocked = !isOnboarding && !!onboardStep && !!startPath

  // The lock is an onboarding affordance, never a correctness guarantee, and
  // the only code that clears it lives in the tour's own "end" step. A tour the
  // user abandons, or one whose iframe never reaches that step, used to leave
  // this full-viewport overlay mounted for good. Release it unconditionally
  // rather than let a third party's iframe decide whether the app stays usable.
  useEffect(() => {
    if (!isLocked) return
    const timer = window.setTimeout(() => setOnboardingStep(null), ONBOARDING_LOCK_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [isLocked, setOnboardingStep])

  // Must mirror Navigation's render conditions (incl. hasAccess), or spacing
  // gets reserved for a banner that never renders (e.g. mid-onboarding).
  const hasAccess = !needsOnboarding(user)
  const hasUpgradeBanner = hasAccess && showsUpgradeBanner(subscription)
  const hasBanner = hasUpgradeBanner || (SHOW_MAINTENANCE_BANNER && maintenace.show)

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      {isLocked && <Loading loading />}
      <Navigation isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <div className="flex">
        <LeftNav setIsCollapsed={setIsCollapsed} isCollapsed={isCollapsed} />
        <div
          id="container"
          className={twMerge(
            styles.container,
            location.pathname === routes.private.onboarding && '!p-0 !h-screen',
            (location.pathname.includes(routes.private.settings) ||
              location.pathname.includes(routes.private.teamSettings) ||
              location.pathname.includes(routes.private.profile) ||
              isFullBleedRoute(location.pathname)) &&
              '!p-0 !overflow-hidden',
            hasBanner && !isOnboarding && styles.banner,
          )}
        >
          <React.Suspense fallback={<Loading loading />}>
            <SlackConnectionPopup />
            <Outlet />
          </React.Suspense>
        </div>
      </div>
    </Sentry.ErrorBoundary>
  )
}

export default Layout
